/**
 * Phase C.1 + C.2 — user profile module.
 *
 * UserProfile is the per-device personalisation payload sent on every chat
 * turn. The backend uses it to seed the system prompt with a role context
 * (so a Sales user doesn't get P&L assumptions) and to append the user's
 * custom standing instructions.
 *
 * Storage: localStorage at PROFILE_KEY. Defaults to an empty profile (no
 * role, no instructions) so the agent behaves exactly as it did before
 * Phase C until the user opens Settings and picks something.
 *
 * Roles are deliberately listed exactly to match the backend's ROLE_SEEDS
 * lookup in bedrock.ts. Adding a role: also add a matching entry there.
 */

const PROFILE_KEY = 'practiceiq.userProfile.v1';
export const CUSTOM_INSTRUCTIONS_MAX_CHARS = 1000;

export interface UserProfile {
  role?: string;
  subcategory?: string;
  customInstructions?: string;
}

export const ROLES: string[] = [
  'Finance',
  'Sales',
  'Marketing',
  'Product',
  'Engineering',
  'HR',
  'Legal',
  'Operations',
  'Data Science',
  'Design',
  'Scientist',
];

export const FINANCE_SUBCATEGORIES: string[] = [
  'Private Equity',
  'Venture Capital',
  'Hedge Fund',
  'Investment Banking',
  'Asset Management',
  'Wealth Management',
  'FP&A',
  'Audit',
  'Sales & Trading',
];

export function loadUserProfile(): UserProfile {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as UserProfile;
    return {
      role: typeof parsed.role === 'string' ? parsed.role : undefined,
      subcategory: typeof parsed.subcategory === 'string' ? parsed.subcategory : undefined,
      customInstructions: typeof parsed.customInstructions === 'string' ? parsed.customInstructions : undefined,
    };
  } catch {
    return {};
  }
}

export function saveUserProfile(profile: UserProfile): void {
  try {
    // Strip empty string fields so the backend sees `undefined`, not `""`,
    // and the role-seed lookup behaves consistently.
    const clean: UserProfile = {};
    if (profile.role && profile.role.trim()) clean.role = profile.role.trim();
    if (profile.subcategory && profile.subcategory.trim()) clean.subcategory = profile.subcategory.trim();
    if (profile.customInstructions && profile.customInstructions.trim()) {
      const ci = profile.customInstructions.trim();
      clean.customInstructions = ci.length > CUSTOM_INSTRUCTIONS_MAX_CHARS
        ? ci.slice(0, CUSTOM_INSTRUCTIONS_MAX_CHARS)
        : ci;
    }
    localStorage.setItem(PROFILE_KEY, JSON.stringify(clean));
  } catch {
    /* private mode — profile won't persist but in-memory state still works */
  }
}

/** True when the user has set any field. Drives the first-run prompt. */
export function isProfileSet(profile: UserProfile): boolean {
  return !!(profile.role || profile.subcategory || (profile.customInstructions && profile.customInstructions.trim()));
}
